import React, { useState, useEffect } from 'react';
import {
  TextField,
  TextFieldProps,
  FormHelperText,
  Box,
} from '@mui/material';
import { useFieldValidation } from '../utils/fieldValidation';

interface ValidatedTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  entity: 'provider' | 'client' | 'visit' | 'service' | 'user';
  field: string;
  isRequired?: boolean;
  onChange?: (value: string, isValid: boolean) => void;
  showCharacterCount?: boolean;
  customValidation?: (value: string) => string | null;
}

const ValidatedTextField: React.FC<ValidatedTextFieldProps> = ({
  entity,
  field,
  isRequired = false,
  onChange,
  showCharacterCount = true,
  customValidation,
  value = '',
  error: externalError,
  helperText: externalHelperText,
  ...textFieldProps
}) => {
  const [internalValue, setInternalValue] = useState(value as string);
  const [touched, setTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const { validate, isValid, maxLength } = useFieldValidation(entity, field, isRequired);
  
  // Update internal value when external value changes
  useEffect(() => {
    setInternalValue(value as string);
  }, [value]);
  
  // Validate on value change
  useEffect(() => {
    if (touched || internalValue) {
      let error: string | null = null;
      
      // Use custom validation if provided
      if (customValidation) {
        error = customValidation(internalValue);
      } else {
        // Use default validation
        error = validate(internalValue);
      }
      
      setValidationError(error);
      
      // Call onChange with validation result
      if (onChange) {
        onChange(internalValue, !error);
      }
    }
  }, [internalValue, touched, validate, customValidation, onChange]);
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInternalValue(newValue);
  };
  
  const handleBlur = () => {
    setTouched(true);
  };
  
  const handleFocus = () => {
    setTouched(true);
  };
  
  // Determine if field should show error state
  const hasError = (touched || internalValue) && (validationError || externalError);
  const errorMessage = validationError || externalError;
  
  // Character count display
  const characterCount = internalValue.length;
  const isOverLimit = characterCount > maxLength;
  
  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        {...textFieldProps}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        error={!!hasError}
        helperText={
          hasError ? errorMessage : 
          showCharacterCount && internalValue ? 
            `${characterCount}/${maxLength} characters` : 
            externalHelperText
        }
        inputProps={{
          ...textFieldProps.inputProps,
          maxLength: maxLength,
        }}
        sx={{
          ...textFieldProps.sx,
          '& .MuiOutlinedInput-root': {
            ...(hasError && {
              '& fieldset': {
                borderColor: '#d32f2f',
              },
              '&:hover fieldset': {
                borderColor: '#d32f2f',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#d32f2f',
              },
            }),
            ...(isOverLimit && !hasError && {
              '& fieldset': {
                borderColor: '#f57c00',
              },
              '&:hover fieldset': {
                borderColor: '#f57c00',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#f57c00',
              },
            }),
          },
          '& .MuiFormHelperText-root': {
            ...(hasError && {
              color: '#d32f2f',
            }),
            ...(isOverLimit && !hasError && {
              color: '#f57c00',
            }),
          },
        }}
      />
      {showCharacterCount && internalValue && !hasError && (
        <FormHelperText 
          sx={{ 
            mt: 0.5,
            color: isOverLimit ? '#f57c00' : 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {characterCount}/{maxLength} characters
          {isOverLimit && ' (over limit)'}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ValidatedTextField;
